/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth, describeAxiosError, formatApiError } from "@/contexts/CustomerAuthContext";
import AuthShell, { Field } from "@/components/customer/AuthShell";
import SEO from "@/components/site/SEO";

export default function ResetPassword() {
  const { resetPassword } = useCustomerAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Missing or invalid reset token."); return; }
    if (pw.length < 12 || !/[a-z]/.test(pw) || !/[A-Z]/.test(pw) || !/\d/.test(pw)) {
      setError("Password must be at least 12 chars and include uppercase, lowercase and a number.");
      return;
    }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await resetPassword(token, pw);
      toast.success("Password updated. Please sign in.");
      nav("/login", { replace: true });
    } catch (err) {
      setError(describeAxiosError(err, "Reset failed."));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell eyebrow="Account recovery" title="Set a new password"
      subtitle="Choose a strong password you don't use anywhere else."
      footer={<><Link to="/login" className="font-semibold text-brand-green hover:text-brand-greenHover">Back to sign in</Link></>}>
      <SEO title="Reset password" path="/reset-password" />
      <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form" noValidate>
        <Field label="New password" type="password" testId="reset-pw" value={pw} onChange={(e)=>setPw(e.target.value)}
               placeholder="Min 12 chars · upper, lower, number" required autoComplete="new-password" name="password" />
        <Field label="Confirm password" type="password" testId="reset-pw2" value={pw2} onChange={(e)=>setPw2(e.target.value)}
               placeholder="Repeat the new password" required autoComplete="new-password" name="password2" />
        {error && <div data-testid="reset-error" className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
        <button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3 text-sm font-semibold text-brand-ink hover:bg-brand-greenHover disabled:opacity-70">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating…</> : "Update password"}
        </button>
      </form>
    </AuthShell>
  );
}
